import { ensureGa4Loaded, sendGa4Event } from './lib/ga4';
import { sanitizeAnalyticsParams } from './lib/sanitizeAnalyticsParams';

export type AnalyticsEvent =
  | 'auth_google_sign_in'
  | 'auth_google_link_prompt_shown'
  | 'auth_google_link_started'
  | 'auth_google_link_completed'
  | 'auth_email_sign_in'
  | 'auth_email_sign_up'
  | 'course_select'
  | 'sync_start'
  | 'sync_success'
  | 'sync_error'
  | 'sync_email_success'
  | 'sync_email_error'
  | 'page_view'
  | 'grading_session_start'
  | 'grade_session_resume'
  | 'scan_capture'
  | 'rubric_created'
  | 'rubric_template_save'
  | 'rubric_template_apply'
  | 'rubric_template_delete'
  | 'lesson_generated'
  | 'lesson_plan_print'
  | 'lesson_plan_share'
  | 'lesson_plan_drive_save'
  | 'lesson_plan_email_admin'
  | 'plan_template_apply'
  | 'message_sent'
  | 'paywall_view'
  | 'subscription_start'
  | 'subscription_refresh'
  | 'elite_event'
  | 'schedule_paper_scan'
  | 'schedule_paper_import'
  | 'feedback_batch_export'
  | 'teach_behavior_log'
  | 'lesson_plan_google_doc_create'
  | 'offline_action_enqueued'
  | 'offline_queue_cleared'
  | 'grade_sync_queued_offline'
  | 'account_delete_completed';

const CONSENT_KEY = 'dg_analytics_consent';

export function getAnalyticsConsent(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    const v = localStorage.getItem(CONSENT_KEY);
    return v === '1';
  } catch {
    return false;
  }
}

export function setAnalyticsConsent(allowed: boolean): void {
  try {
    localStorage.setItem(CONSENT_KEY, allowed ? '1' : '0');
  } catch {
    // ignore
  }
}

/** On app boot and when the user accepts analytics — loads GA4 gtag when `VITE_GA_MEASUREMENT_ID` is set. */
export function syncAnalyticsProvider(): void {
  if (!getAnalyticsConsent()) return;
  ensureGa4Loaded();
}

/**
 * Log event. When consent is false, no-op (or console in dev).
 * When `VITE_GA_MEASUREMENT_ID` is set and consent is true, forwards to GA4 via gtag.
 */
export function logEvent(event: AnalyticsEvent, data: Record<string, unknown> = {}): void {
  const safe = sanitizeAnalyticsParams(data);
  if (!getAnalyticsConsent()) {
    if (import.meta.env?.DEV) console.info('[Analytics] (consent off)', event, safe);
    return;
  }
  ensureGa4Loaded();
  const params: Record<string, string | number> = { event_category: 'donegrading' };
  for (const [k, v] of Object.entries(safe)) {
    if (typeof v === 'string' || typeof v === 'number') params[k] = v;
    else if (v != null) params[k] = String(v).slice(0, 120);
  }
  if (import.meta.env?.DEV) console.info('[Analytics]', event, safe);
  sendGa4Event(event, params);
}
